/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
  useToast,
} from '@chakra-ui/core';
import React, { useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import isEmpty from 'lodash/isEmpty';
import { useCurrentUserProfile } from '../../../../../app/hooks/useCurrentUserProfile';
import { QuestionnaireTemplateStore } from '../../store/questionnaireTemplateModel';
import { TEMPLATE_MODE } from '../../questionnaireTemplate.enums';
import { useFocusFirstError } from '../errorHandlers/useFocusFirstError';
import { PATH_QUESTIONNAIRE_TEMPLATE_EDIT } from '../../../../../routes/constants';

export const QuestionSaveButton = React.memo(function QuestionSaveButton() {
  const { t } = useTranslation('questionnaireTemplate');
  const { currentUserProfile } = useCurrentUserProfile();
  const { templateId } = useParams<{ templateId?: string }>();
  useFocusFirstError();
  const toast = useToast();

  const templateMode = QuestionnaireTemplateStore.useStoreState((s) => s.templateMode);
  const saveTemplate = QuestionnaireTemplateStore.useStoreActions((s) => s.saveTemplate);
  const commitSnapshot = QuestionnaireTemplateStore.useStoreActions((s) => s.commitSnapshot);

  const isLoading = QuestionnaireTemplateStore.useStoreState((s) => s.isLoading);
  const hasErrors = QuestionnaireTemplateStore.useStoreState((s) => s.hasErrors);
  const activePositionCount = QuestionnaireTemplateStore.useStoreState((s) => s.activePositionCount);
  const hasNoElements = QuestionnaireTemplateStore.useStoreState((state) => isEmpty(state.entities.elements));

  const accountId = currentUserProfile?.account ?? '';
  const isDisabled = hasErrors || isLoading || hasNoElements;

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const history = useHistory<unknown>();

  const fetchTemplate = QuestionnaireTemplateStore.useStoreActions((a) => a.fetchTemplate);

  const executeSave = () => {
    const modeAtSaveTime = templateMode;
    saveTemplate({ accountId, toast, templateId, t })
      .then((res) => {
        if (res) {
          toast({
            title:
              templateMode === TEMPLATE_MODE.CREATE
                ? t('formBuilder.elementList.createdSuccessfully')
                : t('formBuilder.elementList.updatedSuccessfully'),
            status: 'success',
            isClosable: true,
          });

          /* istanbul ignore else */
          if (modeAtSaveTime === TEMPLATE_MODE.CREATE || modeAtSaveTime === TEMPLATE_MODE.COPY) {
            const newId = res.data.data;
            const editPath = PATH_QUESTIONNAIRE_TEMPLATE_EDIT.replace(':templateId', newId);
            history.replace(editPath);
          } else {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-floating-promises
            fetchTemplate({ accountId, templateId: templateId!, isCopy: false });
            commitSnapshot();
          }
        }
      })
      .catch((e: AxiosError) => {
        let message = t('formBuilder.elementList.saveError');
        if (e.response?.status === 409) {
          message = e?.response?.data?.errors;
        }
        toast({
          title: message,
          status: 'error',
          isClosable: true,
        });
      });
  };

  const handleSave = () => {
    if (templateMode === TEMPLATE_MODE.EDIT && (activePositionCount ?? 0) > 0) {
      setIsConfirmOpen(true);
      return;
    }
    executeSave();
  };

  const handleConfirmSave = () => {
    setIsConfirmOpen(false);
    executeSave();
  };

  const labelMap: Record<TEMPLATE_MODE, string> = {
    [TEMPLATE_MODE.CREATE]: t('formBuilder.elementList.saveButton.save'),
    [TEMPLATE_MODE.EDIT]: t('formBuilder.elementList.saveButton.update'),
    [TEMPLATE_MODE.COPY]: t('formBuilder.elementList.saveButton.saveAsCopy'),
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        fontSize={12}
        colorScheme="blue"
        onClick={handleSave}
        isLoading={isLoading}
        aria-label={labelMap[templateMode]}
        data-testid="save-template-button"
        isDisabled={isDisabled}
      >
        {labelMap[templateMode]}
      </Button>

      <AlertDialog
        size="lg"
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={/* istanbul ignore next */ () => setIsConfirmOpen(false)}
        isCentered
        closeOnOverlayClick={false}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('formBuilder.elementList.confirmSaveModal.title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('formBuilder.elementList.confirmSaveModal.body', {
                count: activePositionCount ?? 0,
              })}
            </AlertDialogBody>

            <AlertDialogFooter>
              <HStack>
                <Button
                  ref={cancelRef}
                  onClick={() => setIsConfirmOpen(false)}
                  colorScheme="red"
                  data-testid="confirm-save-cancel-button"
                >
                  {t('formBuilder.elementList.confirmSaveModal.cancel')}
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleConfirmSave}
                  isLoading={isLoading}
                  data-testid="confirm-save-proceed-button"
                >
                  {t('formBuilder.elementList.confirmSaveModal.proceed')}
                </Button>
              </HStack>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
});
